import { FormikErrors, useFormik } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/Button';
import CurrencyInput from '../../../components/CurrencyInput/CurrencyInput';
import Modal from '../../../components/Modal/Modal';
import { InventoryItem } from './InventoryTable';
import './ProductDetailsModal.scss';

interface Props {
  isModalOpened: boolean;
  closeModal: () => void;
  item: InventoryItem | undefined;
  onSubmit: (i: InventoryItem) => void;
}

const COLUMN_LABELS: { [key: string]: string } = {
  asin: 'ASIN',
  sku: 'SKU',
  name: 'Name',
  vat_rate: 'VAT Rate',
  customs_number: 'Customs Number',
  weight: 'Weight',
  price: 'Purchase price',
};

interface FormValues {
  price: string;
}

export default function ProductDetailsModal({
  isModalOpened,
  closeModal,
  onSubmit,
  item,
}: Props) {
  const { t } = useTranslation();

  const formik = useFormik<FormValues>({
    initialValues: {
      price: '',
    },
    validate: (values) => {
      const errors: FormikErrors<FormValues> = {};
      if (!values.price) {
        errors.price = t('Required');
      }
      return errors;
    },
    onSubmit: ({ price }) => {
      onSubmit({ ...item!, price: { amount: +price * 100, currency: 'eur' } });
      closeModal();
    },
  });

  useEffect(() => {
    if (isModalOpened) {
      formik.resetForm();
    }
    // eslint-disable-next-line
  }, [isModalOpened]);

  const onAmountChange: (v: string | undefined) => void = (v) => {
    setTimeout(() => {
      formik.setFieldValue('price', v, true);
      formik.setFieldTouched('price', true, true);
    }, 10);
  };

  return (
    <Modal
      isOpened={isModalOpened}
      close={closeModal}
      title={t('Product details')}
    >
      {item && (
        <div className='mx-3 pt-3 pb-4 px-3'>
          <div className='Text--20 fw-800 mb-3'>{t(item.name)}</div>
          {Object.entries(item)
            .filter(([k]) => k !== 'price' && k !== 'id')
            .map(([k, v]) => (
              <div className='row mb-3' key={k}>
                <div className='col-4'>{t(COLUMN_LABELS[k] || '')}</div>
                <div className='col'>{v}</div>
              </div>
            ))}
          <form onSubmit={formik.handleSubmit}>
            <div className='row mb-4 align-items-center'>
              <div className='col-4 pb-3'>{t(COLUMN_LABELS['price'])}</div>
              <div className='col d-flex align-items-center'>
                <CurrencyInput
                  name='price'
                  onValueChange={onAmountChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.price}
                  error={formik.errors.price}
                  touched={formik.touched.price}
                  className='Input--ProductDetailsModal me-4'
                />
                <div className='pb-3'>EUR</div>
              </div>
            </div>
            <div className='row row-cols-auto gx-2 mt-2'>
              <div className='col'>
                <Button
                  className='d-flex align-items-center'
                  type='submit'
                  // disabled={isLoading}
                  data-testid='submit-note-modal-btn'
                >
                  {/* {isLoading && (
                    <MiniLoader className='MiniLoader--sm d-flex align-items-center justify-content-center me-2 MiniLoader--gray' />
                  )} */}
                  {t('Update')}
                </Button>
              </div>
              <div className='col'>
                <Button
                  styling='outline'
                  onClick={closeModal}
                  type='button'
                  // disabled={isLoading}
                >
                  {t('Cancel')}
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}
    </Modal>
  );
}
